import {
  Shield,
  Copy,
  Check,
  Calendar,
  Activity,
  Camera,
  FileText,
  FileEdit,
  QrCode,
  Sparkles,
  Brain,
  Bell,
  Lock,
  Cpu,
  ExternalLink,
  MoreVertical,
  UserX,
  ArrowRightLeft,
  RefreshCw,
  AlertTriangle,
  CircleCheck,
  Baby,
  Users,
  Pencil,
  X,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useCallback } from 'react';
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../types/pet';
import { DIDCreation } from './DIDCreation';
import { DIDManagement } from './DIDManagement';
import { HealthCareRecords } from './HealthCareRecords';
import { QRHub } from './QRHub';
import { MintNFTFlow } from './nft/MintNFTFlow';
import { NFTGallery } from './nft/NFTGallery';
import { BookingFlow } from './appointments/BookingFlow';
import { MicrochipLinking, MicrochipData } from './microchip/MicrochipLinking';
import { MicrochipManagement } from './microchip/MicrochipManagement';
import { MicrochipStatusCard } from './microchip/MicrochipStatusCard';
import { ZKPVerificationManager } from './zkp/ZKPVerificationManager';
import { AIConsentFlow } from './ai/AIConsentFlow';
import { AIHealthInsights } from './ai/AIHealthInsights';
import { PendingApprovals } from './owner/PendingApprovals';
import { OwnershipOverviewCard } from './owner/OwnershipOverviewCard';
import { TransferOwnershipModal } from './family/TransferOwnershipModal';
import { ReportLostModal } from './family/ReportLostModal';
import { EditPetProfileModal } from './EditPetProfileModal';
import { MediaEditorModal } from './social/MediaEditorModal';
import { copyToClipboard } from '../utils/clipboard';
import { usePets } from '../context/PetsContext';
import { useProposedRecords } from '../context/ProposedRecordsContext';
import { useNotifications } from '../context/NotificationsContext';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';
import { usePetPhoto } from '../hooks/usePetPhoto';
import { countries, countriesAndCities } from '../data/countries-cities';
import {
  linkMicrochip,
  unlinkMicrochip as unlinkMicrochipApi,
  updateMicrochipSettings,
  getPetById,
  updatePet as updatePetApi,
  revokeDid,
  restoreDid,
  initPetPhotoUpload,
  uploadPetPhoto,
  markFound,
  type LostStatus,
} from '../../../api/pets';
import type { OwnershipOverview } from '../../../api/pets';

interface PetOverviewProps {
  pet: Pet;
  isOnboardingComplete?: boolean;
  /** Called when Health Records overlay is closed so the app can e.g. ensure Overview tab is active */
  onCloseHealthRecords?: () => void;
}

export function PetOverview({
  pet,
  isOnboardingComplete = true,
  onCloseHealthRecords,
}: PetOverviewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePet, refreshPets, pets, setSelectedPetId } = usePets();
  const { addNotification } = useNotifications();
  const hasParents = !!(pet.parents?.mother || pet.parents?.father);
  const offspring = useMemo(
    () =>
      pets.filter(
        (p) =>
          p.id !== pet.id &&
          (p.parents?.mother === pet.did || p.parents?.father === pet.did)
      ),
    [pets, pet.id, pet.did]
  );
  const motherPet = useMemo(
    () =>
      pet.parents?.mother
        ? pets.find((p) => p.did === pet.parents?.mother)
        : null,
    [pets, pet.parents?.mother]
  );
  const fatherPet = useMemo(
    () =>
      pet.parents?.father
        ? pets.find((p) => p.did === pet.parents?.father)
        : null,
    [pets, pet.parents?.father]
  );
  const { getProposedRecordsForPet, loadProposedRecordsForPet } = useProposedRecords();
  const [copied, setCopied] = useState(false);
  const [showDIDCreation, setShowDIDCreation] = useState(false);
  const [showDIDManagement, setShowDIDManagement] = useState(false);
  const [showHealthRecords, setShowHealthRecords] = useState(false);
  const [showQRHub, setShowQRHub] = useState(false);
  const [showMintNFT, setShowMintNFT] = useState(false);
  const [showNFTGallery, setShowNFTGallery] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [showMicrochipLinking, setShowMicrochipLinking] = useState(false);
  const [showMicrochipManagement, setShowMicrochipManagement] = useState(false);
  const [showZKPManager, setShowZKPManager] = useState(false);
  const [showAIConsent, setShowAIConsent] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [showPendingApprovals, setShowPendingApprovals] = useState(false);
  const [showDIDMenu, setShowDIDMenu] = useState(false);
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] =
    useState(false);
  const [editingPetName, setEditingPetName] = useState(false);
  const [editPetNameValue, setEditPetNameValue] = useState(pet.name);
  const [nameSaveLoading, setNameSaveLoading] = useState(false);

  const canEditName = !pet.isCaretaker;
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [didLoading, setDidLoading] = useState(false);
  const [didStatus, setDidStatus] = useState<'active' | 'revoked'>(
    pet.didStatus || 'active'
  );
  const [aiEnabled, setAiEnabled] = useState(pet.aiEnabled || false);
  const {
    signedImageUrl,
    setSignedImageUrl,
    fetchSignedPhoto: fetchSignedPhotoWithHook,
    lastFetchedPhotoRef,
  } = usePetPhoto({
    petId: pet.id,
    photoKey: pet.photoKey,
    imageUrl: pet.imageUrl,
  });
  const [isUploadingPetPhoto, setIsUploadingPetPhoto] = useState(false);
  const petPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const [microchipData, setMicrochipData] = useState<
    (MicrochipData & { linkedDate: string }) | null
  >(null);
  const [, setMicrochipLoading] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [showReportLostModal, setShowReportLostModal] = useState(false);
  const [showMarkFoundConfirm, setShowMarkFoundConfirm] = useState(false);
  const [lostStatus, setLostStatus] = useState<LostStatus>(
    pet.lostStatus || 'active'
  );
  const [lostLoading, setLostLoading] = useState(false);
  const [isDeceased, setIsDeceased] = useState<boolean>(
    pet.isDeceased || false
  );
  const [deceasedLoading] = useState(false);
  const [ownershipOverview, setOwnershipOverview] =
    useState<OwnershipOverview | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<
    'PRIMARY_OWNER' | 'CO_OWNER' | 'CARETAKER' | null
  >(null);

  // Load pet data from backend on mount and when pet changes
  useEffect(() => {
    // Flag to prevent multiple concurrent fetches or unnecessary updates
    let isMounted = true;

    // Reset microchip data when switching pets to prevent stale data
    if (lastFetchedPhotoRef.current.id !== pet.id) {
      setMicrochipData(null);
    }

    const loadPetData = async () => {
      if (!isMounted) return;

      setMicrochipLoading(true);
      try {
        const response = await getPetById(pet.id);
        if (!isMounted) return;

        const fullPet = response.pet;

        // Sync DID status from database
        if (fullPet.didStatus) {
          setDidStatus(fullPet.didStatus);
        }

        // Sync lost status from database
        if (fullPet.lostStatus) {
          setLostStatus(fullPet.lostStatus);
        }

        // Sync deceased status from database
        if (fullPet.isDeceased !== undefined) {
          setIsDeceased(fullPet.isDeceased);
        }

        if (fullPet.microchip) {
          setMicrochipData({
            chipNumber: fullPet.microchip.chipNumber,
            standard: fullPet.microchip.standard,
            implantDate: fullPet.microchip.implantDate,
            implantLocation: fullPet.microchip.implantLocation,
            linkToDID: fullPet.microchip.linkToDID,
            visibility: fullPet.microchip.visibility,
            linkedDate: new Date().toISOString(), // Placeholder since we don't store this
          });
        } else {
          // Explicitly clear microchip data if pet has no microchip
          setMicrochipData(null);
        }

        // Sync local context with full pet data so Family Tree and other views stay in sync
        const contextUpdates: Partial<Parameters<typeof updatePet>[1]> = {};
        if (
          fullPet.photoKey !== pet.photoKey ||
          fullPet.imageUrl !== pet.imageUrl
        ) {
          contextUpdates.photoKey = fullPet.photoKey;
          contextUpdates.imageUrl = fullPet.imageUrl;
        }
        const rawLost =
          fullPet.lostStatus ??
          (fullPet as { lost_status?: string }).lost_status;
        if (rawLost !== undefined && rawLost !== pet.lostStatus) {
          const normalized = String(rawLost).toLowerCase().trim();
          if (
            normalized === 'active' ||
            normalized === 'lost' ||
            normalized === 'recovered'
          ) {
            contextUpdates.lostStatus = normalized;
          }
        }
        if (
          fullPet.isDeceased !== undefined &&
          fullPet.isDeceased !== pet.isDeceased
        ) {
          contextUpdates.isDeceased = fullPet.isDeceased;
        }
        if (Object.keys(contextUpdates).length > 0) {
          updatePet(pet.id, contextUpdates);
        }

        setOwnershipOverview(fullPet.ownershipOverview ?? null);
        setCurrentUserRole(fullPet.currentUserRole ?? null);
      } catch (error) {
        console.error('Failed to load pet data:', error);
        if (isMounted) setMicrochipData(null);
      } finally {
        if (isMounted) setMicrochipLoading(false);
      }
    };
    loadPetData();

    return () => {
      isMounted = false;
    };
  }, [pet.id, pet.photoKey, pet.imageUrl, updatePet, fetchSignedPhotoWithHook]);

  // Refetch microchip when E-Passport (or elsewhere) links/updates/unlinks
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ petId: string }>;
      if (ev.detail?.petId === pet.id) {
        getPetById(pet.id)
          .then(({ pet: fullPet }) => {
            if (fullPet.microchip) {
              setMicrochipData({
                chipNumber: fullPet.microchip.chipNumber,
                standard: fullPet.microchip.standard,
                implantDate: fullPet.microchip.implantDate,
                implantLocation: fullPet.microchip.implantLocation,
                linkToDID: fullPet.microchip.linkToDID,
                visibility: fullPet.microchip.visibility,
                linkedDate: new Date().toISOString(),
              });
            } else {
              setMicrochipData(null);
            }
          })
          .catch(() => setMicrochipData(null));
      }
    };
    window.addEventListener('microchip-updated', handler);
    return () => window.removeEventListener('microchip-updated', handler);
  }, [pet.id]);

  const refreshOwnership = useCallback(async () => {
    try {
      const response = await getPetById(pet.id);
      setOwnershipOverview(response.pet.ownershipOverview ?? null);
      setCurrentUserRole(response.pet.currentUserRole ?? null);
      await refreshPets();
    } catch {
      // Non-critical
    }
  }, [pet.id, refreshPets]);

  // Fallback when API doesn't return ownership (e.g. not yet loaded or legacy): show card so "Add Owner or Caretaker" works
  const effectiveOwnershipOverview =
    ownershipOverview ??
    (pet.owner
      ? {
          primaryOwner: {
            email: pet.owner.email,
            fullName:
              pet.owner.name ||
              pet.owner.email ||
              t('petOverview.primaryOwner'),
          },
          secondaryOwners: [] as {
            email: string;
            role: 'CO_OWNER' | 'CARETAKER';
            status: 'PENDING' | 'ACTIVE';
          }[],
        }
      : null);
  const effectiveCurrentUserRole =
    currentUserRole ?? (pet.owner ? 'PRIMARY_OWNER' : null);

  useEffect(() => {
    setEditPetNameValue(pet.name);
  }, [pet.id, pet.name]);

  const handleStartEditName = () => {
    setEditPetNameValue(pet.name);
    setEditingPetName(true);
  };

  const handleCancelEditName = () => {
    setEditPetNameValue(pet.name);
    setEditingPetName(false);
  };

  const handleSavePetName = async () => {
    const trimmed = editPetNameValue.trim();
    if (!trimmed) {
      toast.error(t('petNameEdit.required', 'Name is required.'));
      return;
    }
    if (trimmed === pet.name) {
      setEditingPetName(false);
      return;
    }
    setNameSaveLoading(true);
    try {
      await updatePetApi(pet.id, { name: trimmed });
      updatePet(pet.id, { name: trimmed });
      await refreshPets();
      setEditingPetName(false);
      addNotification({
        type: 'record_added',
        title: 'Overview updated',
        message: `Pet name was updated to "${trimmed}".`,
        petId: pet.id,
        petName: trimmed,
        icon: '👤',
        source: 'Overview',
      });
      toast.success(t('petNameEdit.saved', t('petNameEdit.saved')));
    } catch (err) {
      console.error('Failed to update pet name:', err);
      toast.error(
        t('petNameEdit.error', 'Failed to update name. Please try again.')
      );
    } finally {
      setNameSaveLoading(false);
    }
  };

  const [photoToEdit, setPhotoToEdit] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);

  const handlePetPhotoPick = () => {
    if (isUploadingPetPhoto) return;
    petPhotoInputRef.current?.click();
  };

  const handlePetPhotoSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('petPhotoUpload.toast.invalidFileType'));
      return;
    }
    setPhotoToEdit({ file, previewUrl: URL.createObjectURL(file) });
  };

  const uploadPetPhotoFile = async (file: File) => {
    setIsUploadingPetPhoto(true);
    const previousSignedImageUrl = signedImageUrl;
    const previousPhotoKey = pet.photoKey;
    const localPreviewUrl = URL.createObjectURL(file);
    let shouldRevokePreview = true;
    setSignedImageUrl(localPreviewUrl);

    try {
      const contentType = file.type || 'application/octet-stream';
      let init: { key: string };
      try {
        init = await initPetPhotoUpload({
          petId: pet.id,
          filename: file.name,
          contentType,
        });
      } catch (err: unknown) {
        console.error('Pet photo upload init failed:', err);
        const errWithResponse = err as {
          response?: {
            status?: unknown;
            data?: { message?: unknown; error?: unknown };
          };
        };
        const status =
          typeof errWithResponse.response?.status === 'number'
            ? errWithResponse.response.status
            : undefined;
        const serverMessage =
          (typeof errWithResponse.response?.data?.message === 'string' &&
            errWithResponse.response.data.message) ||
          (typeof errWithResponse.response?.data?.error === 'string' &&
            errWithResponse.response.data.error) ||
          undefined;
        toast.error(
          serverMessage ||
            (status
              ? t('petPhotoUpload.toast.failedInitWithStatus', {
                  status: String(status),
                })
              : t('petPhotoUpload.toast.failedInit'))
        );
        throw err;
      }

      try {
        await uploadPetPhoto(init.key, file, contentType);
      } catch (err: unknown) {
        console.error('Pet photo upload PUT failed:', err);
        toast.error(t('petPhotoUpload.toast.failedUpload'));
        throw err;
      }

      // Persist photoKey after successful object upload so backend and client stay in sync.
      await updatePetApi(pet.id, { photoKey: init.key });
      updatePet(pet.id, { photoKey: init.key });

      // Refresh with a real signed URL
      try {
        const url = await fetchSignedPhotoWithHook(
          pet.id,
          init.key,
          pet.imageUrl
        );
        if (url) {
          setSignedImageUrl(url);
          // We've swapped away from the local preview; safe to revoke it now.
          URL.revokeObjectURL(localPreviewUrl);
          shouldRevokePreview = false;
        } else {
          // Keep local preview if signing is temporarily unavailable.
          shouldRevokePreview = false;
        }
      } catch {
        // keep local preview if signing fails
        shouldRevokePreview = false;
      }

      toast.success(t('petPhotoUpload.toast.updated'));
    } catch (err) {
      console.error('Failed to update pet photo:', err);
      // Revert visual preview and local photoKey without pulling potentially stale server state.
      setSignedImageUrl(previousSignedImageUrl);
      updatePet(pet.id, { photoKey: previousPhotoKey });
    } finally {
      setIsUploadingPetPhoto(false);
      if (shouldRevokePreview) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    }
  };

  // Sync lostStatus and isDeceased when pet data changes
  useEffect(() => {
    setLostStatus(pet.lostStatus || 'active');
    setIsDeceased(pet.isDeceased || false);
  }, [pet.id, pet.lostStatus, pet.isDeceased]);

  // Load proposed records (certificates, health records from professionals) for this pet
  useEffect(() => {
    if (pet.id) {
      loadProposedRecordsForPet(pet.id);
    }
  }, [pet.id, loadProposedRecordsForPet]);

  const [editCountry, setEditCountry] = useState(pet.country || '');
  const [editCity, setEditCity] = useState(pet.city || '');
  const [editCustomCity, setEditCustomCity] = useState('');
  const vaccinations: any[] = [];

  // Get pending proposals for this pet
  const allProposals = getProposedRecordsForPet(pet.id);
  const pendingProposals = allProposals.filter(
    (r) => r.status === 'proposed'
  );
  console.log('[PetOverview] pet.id:', pet.id, 'allProposals:', allProposals.length, 'pending:', pendingProposals.length);

  const copyDID = () => {
    copyToClipboard(pet.did);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveLocation = () => {
    const cityToSave = editCity === 'Other' ? editCustomCity : editCity;
    updatePet(pet.id, {
      country: editCountry,
      city: cityToSave,
    });
    addNotification({
      type: 'record_added',
      title: 'Overview updated',
      message: `Location for ${pet.name} was updated to ${cityToSave || editCountry}.`,
      petId: pet.id,
      petName: pet.name,
      icon: '📍',
      source: 'Overview',
    });
    setEditingLocation(false);
  };

  const handleCancelLocation = () => {
    setEditCountry(pet.country || '');
    setEditCity(pet.city || '');
    setEditCustomCity('');
    setEditingLocation(false);
  };

  const age = Math.floor(
    (new Date().getTime() - new Date(pet.dateOfBirth).getTime()) /
      (365.25 * 24 * 60 * 60 * 1000)
  );

  if (showDIDManagement) {
    return (
      <DIDManagement
        onClose={() => setShowDIDManagement(false)}
        onSave={refreshPets}
        petData={{
          id: pet.id,
          name: pet.name,
          species: pet.species,
          breed: pet.breed,
          did: pet.did,
          didStatus: didStatus,
          // Pass saved DID permissions
          didPermissionsViewData: pet.didPermissionsViewData,
          didPermissionsAmendFields: pet.didPermissionsAmendFields,
          didPermissionsIssueCredentials: pet.didPermissionsIssueCredentials,
          didPermissionsTimeBound: pet.didPermissionsTimeBound,
          didPermissionsExpiryDate: pet.didPermissionsExpiryDate,
        }}
      />
    );
  }

  if (showDIDCreation) {
    return (
      <DIDCreation
        onClose={() => setShowDIDCreation(false)}
        petData={{
          id: pet.id,
          name: pet.name,
          species: pet.species,
          breed: pet.breed,
          country: pet.country || '',
        }}
        onComplete={(did) => {
          console.log('DID created:', did);
          // Update local state with new DID
          updatePet(pet.id, { did, didStatus: 'active' });
          setDidStatus('active');
          setShowDIDCreation(false);
        }}
      />
    );
  }

  if (showHealthRecords) {
    return (
      <HealthCareRecords
        pet={pet}
        onClose={() => {
          setShowHealthRecords(false);
          onCloseHealthRecords?.();
        }}
      />
    );
  }

  if (showQRHub) {
    return (
      <QRHub
        petName={pet.name}
        petId={pet.id}
        onClose={() => setShowQRHub(false)}
      />
    );
  }

  if (showMintNFT) {
    return (
      <MintNFTFlow
        pet={pet}
        onClose={() => {
          setShowMintNFT(false);
          // Open gallery after closing mint flow so user can see their NFTs
          setShowNFTGallery(true);
        }}
        onComplete={(nft) => {
          console.log('NFT minted:', nft);
          // Don't close the modal — let the user see the success screen
          // with transaction details, explorer link, etc.
          // The user can close via "Back to Profile" or "Mint Another" buttons.
        }}
      />
    );
  }

  if (showNFTGallery) {
    return <NFTGallery petId={pet.id} onClose={() => setShowNFTGallery(false)} />;
  }

  if (showBooking) {
    return (
      <BookingFlow onClose={() => setShowBooking(false)} initialPet={pet} />
    );
  }

  if (showZKPManager) {
    return (
      <ZKPVerificationManager
        petId={pet.id}
        onClose={() => setShowZKPManager(false)}
      />
    );
  }

  if (showAIConsent) {
    return (
      <AIConsentFlow
        pets={pets.map((p) => ({
          id: p.id,
          name: p.name,
          did: p.did,
          species: p.species,
          breed: p.breed,
        }))}
        initialPetId={pet.id}
        onComplete={(_settings) => {
          setAiEnabled(true);
          setShowAIConsent(false);
          setShowAIInsights(true);
        }}
        onClose={() => setShowAIConsent(false)}
      />
    );
  }

  if (showAIInsights) {
    return (
      <AIHealthInsights
        pets={pets}
        initialPet={pet}
        aiEnabled={aiEnabled}
        onClose={() => setShowAIInsights(false)}
      />
    );
  }

  if (showPendingApprovals) {
    return (
      <PendingApprovals
        petId={pet.id}
        petName={pet.name}
        onClose={() => setShowPendingApprovals(false)}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full min-w-0 px-0 sm:px-0">
      <div className="mb-6 sm:mb-8">
        <h1
          className="text-2xl sm:text-3xl mb-4 text-[#2D2F7E] dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {t('petOverview.title')}
        </h1>

        {/* Pending Approvals Banner — shown at top for visibility */}
        {pendingProposals.length > 0 && (
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl shadow-lg p-4 sm:p-6 mb-6 text-white">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 animate-pulse" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl mb-0.5">
                    {pendingProposals.length} Record{pendingProposals.length > 1 ? 's' : ''} Pending Approval
                  </h3>
                  <p className="text-xs sm:text-sm text-orange-100 truncate">
                    Your professional has proposed health records for {pet.name}
                  </p>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {pendingProposals.slice(0, 3).map((record) => (
                      <span key={record.id} className="text-xs bg-white/20 px-2 py-0.5 rounded">
                        {record.recordType === 'vaccine' ? '💉' : record.recordType === 'grooming' ? '✂️' : record.recordType === 'medical' ? '⚕️' : '📋'} {record.recordType}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowPendingApprovals(true)}
                className="flex items-center gap-2 bg-white text-orange-600 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap text-sm sm:text-base font-medium flex-shrink-0"
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                Review Now
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          <button
            onClick={() => isOnboardingComplete && setShowZKPManager(true)}
            disabled={!isOnboardingComplete}
            className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation text-left sm:text-center"
            style={{
              background: isOnboardingComplete
                ? 'linear-gradient(to right, rgb(147, 51, 234), rgb(219, 39, 119))'
                : 'linear-gradient(to right, rgb(156, 163, 175), rgb(107, 114, 128))',
              opacity: isOnboardingComplete ? 1 : 0.5,
              cursor: isOnboardingComplete ? 'pointer' : 'not-allowed',
            }}
          >
            {!isOnboardingComplete && <Lock className="w-4 h-4" />}
            <Shield className="w-5 h-5" />
            {t('petOverview.privacyVerification')}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowBooking(true)}
              className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation"
              style={{
                background:
                  'linear-gradient(to right, rgb(22, 163, 74), rgb(5, 150, 105))',
              }}
            >
              <Calendar className="w-5 h-5" />
              {t('petOverview.bookForThisPet')}
            </button>
          </div>
          <button
            onClick={() => isOnboardingComplete && setShowQRHub(true)}
            disabled={!isOnboardingComplete}
            className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation text-left sm:text-center"
            style={{
              background: isOnboardingComplete
                ? 'linear-gradient(to right, rgb(79, 70, 229), rgb(147, 51, 234))'
                : 'linear-gradient(to right, rgb(156, 163, 175), rgb(107, 114, 128))',
              opacity: isOnboardingComplete ? 1 : 0.5,
              cursor: isOnboardingComplete ? 'pointer' : 'not-allowed',
            }}
          >
            {!isOnboardingComplete && <Lock className="w-4 h-4" />}
            <QrCode className="w-5 h-5" />
            {t('petOverview.shareAccess')}
          </button>
          <button
            onClick={() => isOnboardingComplete && setShowHealthRecords(true)}
            disabled={!isOnboardingComplete}
            className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation text-left sm:text-center"
            style={{
              background: isOnboardingComplete
                ? 'linear-gradient(to right, rgb(37, 99, 235), rgb(147, 51, 234))'
                : 'linear-gradient(to right, rgb(156, 163, 175), rgb(107, 114, 128))',
              opacity: isOnboardingComplete ? 1 : 0.5,
              cursor: isOnboardingComplete ? 'pointer' : 'not-allowed',
            }}
          >
            {!isOnboardingComplete && <Lock className="w-4 h-4" />}
            <FileText className="w-5 h-5" />
            {t('petOverview.healthRecords')}
          </button>
          <button
            onClick={() => isOnboardingComplete && setShowAIConsent(true)}
            disabled={!isOnboardingComplete}
            className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation text-left sm:text-center"
            style={{
              background: isOnboardingComplete
                ? 'linear-gradient(to right, rgb(8, 145, 178), rgb(37, 99, 235))'
                : 'linear-gradient(to right, rgb(156, 163, 175), rgb(107, 114, 128))',
              opacity: isOnboardingComplete ? 1 : 0.5,
              cursor: isOnboardingComplete ? 'pointer' : 'not-allowed',
            }}
          >
            {!isOnboardingComplete && <Lock className="w-4 h-4" />}
            <Brain className="w-5 h-5" />
            {t('petOverview.aiConsent')}
          </button>
          <button
            onClick={() => isOnboardingComplete && setShowAIInsights(true)}
            disabled={!isOnboardingComplete}
            className="flex items-center justify-center gap-2 text-white px-4 py-3 min-h-[48px] rounded-lg hover:opacity-90 transition-opacity relative w-full touch-manipulation text-left sm:text-center"
            style={{
              background: isOnboardingComplete
                ? 'linear-gradient(to right, rgb(8, 145, 178), rgb(37, 99, 235))'
                : 'linear-gradient(to right, rgb(156, 163, 175), rgb(107, 114, 128))',
              opacity: isOnboardingComplete ? 1 : 0.5,
              cursor: isOnboardingComplete ? 'pointer' : 'not-allowed',
            }}
          >
            {!isOnboardingComplete && <Lock className="w-4 h-4" />}
            <Brain className="w-5 h-5" />
            {t('petOverview.aiInsights')}
          </button>
        </div>
      </div>

      {/* Birth Banner – for puppies (pet with parents) */}
      {hasParents && pet.dateOfBirth && (
        <div className="bg-gradient-to-r from-amber-400/90 to-pink-400/90 dark:from-amber-600/90 dark:to-pink-600/90 rounded-xl shadow-lg p-4 mb-8 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Baby className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">
                {t('family.birth.bornOn', {
                  date: new Date(pet.dateOfBirth).toLocaleDateString(),
                })}
              </h3>
              <p className="text-sm opacity-90">
                {t('family.birth.parents', 'Parents')}:{' '}
                {motherPet?.name ??
                  (pet.parents?.mother ? t('petOverview.mother') : '—')}{' '}
                &{' '}
                {fatherPet?.name ??
                  (pet.parents?.father ? t('petOverview.father') : '—')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lost Pet Alert Banner - stacks on mobile so "Mark as Found" is never cut off */}
      {lostStatus === 'lost' && (
        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl shadow-lg p-4 sm:p-6 mb-8 text-white min-w-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 animate-pulse" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold mb-1">
                  {t('petOverview.isReportedAsLost', { name: pet.name })}
                </h3>
                <p className="text-sm text-red-100">
                  {t('petOverview.lostBannerBody', { name: pet.name })}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowMarkFoundConfirm(true)}
              disabled={lostLoading}
              className="flex items-center justify-center gap-2 bg-white text-red-600 px-4 py-3 sm:px-6 sm:py-3 rounded-lg hover:bg-red-50 transition-colors font-medium flex-shrink-0 w-full sm:w-auto"
            >
              <CircleCheck className="w-5 h-5" />
              {t('petOverview.markAsFound')}
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Pet Image Card */}
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8">
          <div className="relative w-full h-64 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg mb-4 flex items-center justify-center text-6xl overflow-hidden">
            {signedImageUrl ? (
              <img
                src={signedImageUrl}
                alt={pet.name}
                className="w-full h-full object-cover"
              />
            ) : pet.species === 'dog' ? (
              '🐕'
            ) : (
              '🐈'
            )}

            <button
              type="button"
              onClick={handlePetPhotoPick}
              disabled={isUploadingPetPhoto}
              aria-label={t('petPhotoUpload.aria.update')}
              title={t('petPhotoUpload.aria.update')}
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[#2563EB] shadow-lg flex items-center justify-center transition-transform hover:scale-[1.03] active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isUploadingPetPhoto ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-white" />
              )}
            </button>

            <input
              ref={petPhotoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePetPhotoSelected}
            />
          </div>
          <div className="mb-2">
            {editingPetName ? (
              <div className="flex flex-col items-center gap-2">
                <input
                  type="text"
                  value={editPetNameValue}
                  onChange={(e) => setEditPetNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSavePetName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  className="w-full max-w-[240px] px-3 py-2 text-center text-xl font-semibold border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  disabled={nameSaveLoading}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSavePetName}
                    disabled={nameSaveLoading || !editPetNameValue.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {nameSaveLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {t('common.save', 'Save')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEditName}
                    disabled={nameSaveLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <h2 className="text-2xl text-center dark:text-white">
                  {pet.name}
                </h2>
                {canEditName && (
                  <button
                    type="button"
                    onClick={handleStartEditName}
                    className="p-1.5 rounded-lg text-gray-500 dark:text-white/50 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title={t('petNameEdit.editName')}
                    aria-label={t('petNameEdit.editName', 'Edit name')}
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
          <p className="text-gray-600 dark:text-white/70 text-center mb-4">
            {pet.breed}
          </p>

          {/* Edit profile – same 4 sections as Add Pet (Basic Identity, Ownership, Health, Lifestyle) */}
          {canEditName && (
            <button
              type="button"
              onClick={() => setShowEditProfile(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-white/20 text-gray-600 dark:text-white/70 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mb-4"
              title={t('petOverview.editPetProfile', 'Edit Pet Profile')}
            >
              <FileEdit className="w-5 h-5" />
              <span className="text-sm font-medium">
                {t('petOverview.editProfile', 'Manage Profile')}
              </span>
            </button>
          )}

          {/* NFT Button - Clearly separated */}
          <div className="border-t border-gray-200 dark:border-white/8 pt-4 mt-auto">
            <button
              onClick={() => setShowMintNFT(true)}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-3 rounded-lg hover:opacity-90 transition-opacity mb-2"
            >
              <Sparkles className="w-5 h-5" />
              {t('petOverview.mintFunNft')}
            </button>
            <p className="text-xs text-center text-gray-500 dark:text-white/50">
              {t('petOverview.nftJustForFunNotice')}
            </p>
            <button
              onClick={() => setShowNFTGallery(true)}
              className="w-full mt-2 text-sm text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline"
            >
              {t('petOverview.viewNftGallery')}
            </button>
          </div>
        </div>

        {/* Basic Info Card */}
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl dark:text-white">
              {t('petOverview.basicInformation')}
            </h3>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.species')}
              </div>
              <div className="capitalize dark:text-white">{t(`petOnboarding.step1.${pet.species}`, pet.species)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.breed')}
              </div>
              <div className="dark:text-white">{pet.breed}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.birthdate')}
              </div>
              <div className="dark:text-white">
                {new Date(pet.dateOfBirth).toLocaleDateString()} (
                {t('petOverview.yearsOld', {
                  count: age,
                  defaultValue: `${age} years old`,
                })}
                )
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.gender')}
              </div>
              <div className="capitalize dark:text-white">{t(`petOnboarding.step1.${pet.gender}`, pet.gender)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.color')}
              </div>
              <div className="dark:text-white">{pet.color}</div>
            </div>

            {/* Pet Status Section */}
            <div className="pt-3 border-t border-gray-200 dark:border-white/8">
              <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                {t('petOverview.petStatus')}
              </div>
              <div className="flex items-center gap-4 mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="petStatus"
                    checked={!isDeceased}
                    onChange={() => {
                      setIsDeceased(false);
                      updatePet(pet.id, {
                        isDeceased: false,
                        deceasedAt: undefined,
                      });
                    }}
                    disabled={deceasedLoading}
                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  />
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="dark:text-white">
                    {t('petOverview.alive')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="petStatus"
                    checked={isDeceased}
                    onChange={() => {
                      setIsDeceased(true);
                      updatePet(pet.id, {
                        isDeceased: true,
                        deceasedAt: new Date().toISOString(),
                      });
                    }}
                    disabled={deceasedLoading}
                    className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                  />
                  <span className="w-3 h-3 rounded-full bg-gray-500 dark:bg-gray-400"></span>
                  <span className="dark:text-white">
                    {t('petOverview.deceased')}
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {t('petOverview.deceasedDescription')}
              </p>

              {/* Deceased Info Box */}
              {isDeceased && (
                <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 flex-shrink-0 mt-0.5">
                      <span className="text-gray-500 dark:text-gray-400">
                        🕊️
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                        {t('petOverview.deceasedInfoTitle', { name: pet.name })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('petOverview.deceasedInfoBody')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Lost Pet Section */}
            <div className="pt-3 border-t border-gray-200 dark:border-white/8">
              <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                {t('petOverview.lostPetStatus')}
              </div>

              {lostStatus === 'lost' ? (
                <>
                  {/* Lost Status Info */}
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700/30 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                          {t('petOverview.lostStatusCurrentlyLost', {
                            name: pet.name,
                          })}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {t('petOverview.lostStatusContactYou', {
                            name: pet.name,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mark as Found Button */}
                  <button
                    onClick={() => setShowMarkFoundConfirm(true)}
                    disabled={lostLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {lostLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CircleCheck className="w-4 h-4" />
                    )}
                    {t('petOverview.petIsFound')}
                  </button>
                </>
              ) : (
                <>
                  {/* Report Lost Button */}
                  <button
                    onClick={() => setShowReportLostModal(true)}
                    disabled={lostLoading || isDeceased}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {lostLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    {t('petOverview.reportPetAsLost')}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-white/50 mt-2">
                    {isDeceased
                      ? t('petOverview.cannotReportDeceased')
                      : t('petOverview.reportLostDescription')}
                  </p>
                </>
              )}
            </div>

            {/* Amendable Location Section */}
            <div className="pt-3 border-t border-gray-200 dark:border-white/8">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-500 dark:text-white/50">
                  {t('petOverview.locationAmendable')}
                </div>
                {!editingLocation && (
                  <button
                    onClick={() => setEditingLocation(true)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    {t('common.edit')}
                  </button>
                )}
              </div>

              {!editingLocation ? (
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-400 dark:text-white/40">
                      {t('petOverview.country')}
                    </div>
                    <div className="dark:text-white">
                      {pet.country || t('petOverview.notSpecified')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 dark:text-white/40">
                      {t('petOverview.city')}
                    </div>
                    <div className="dark:text-white">
                      {pet.city || t('petOverview.notSpecified')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-white/70 mb-1">
                      {t('petOverview.country')}
                    </label>
                    <select
                      value={editCountry}
                      onChange={(e) => {
                        setEditCountry(e.target.value);
                        setEditCity('');
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-[#0F0E16] dark:text-white"
                    >
                      <option value="">{t('petOverview.selectCountry')}</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 dark:text-white/70 mb-1">
                      {t('petOverview.cityOptional')}
                    </label>
                    <select
                      value={editCity}
                      onChange={(e) => {
                        setEditCity(e.target.value);
                        if (e.target.value !== 'Other') {
                          setEditCustomCity('');
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-white/5 bg-white dark:bg-[#0F0E16] dark:text-white"
                      disabled={!editCountry}
                    >
                      <option value="">
                        {editCountry
                          ? t('petOverview.selectCityOptional')
                          : t('petOverview.selectCountryFirst')}
                      </option>
                      {editCountry &&
                        countriesAndCities[editCountry]?.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      {editCountry && (
                        <option value="Other">{t('petOverview.other')}</option>
                      )}
                    </select>
                  </div>

                  {editCity === 'Other' && (
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-white/70 mb-1">
                        {t('petOverview.enterCityName')}
                      </label>
                      <input
                        type="text"
                        value={editCustomCity}
                        onChange={(e) => setEditCustomCity(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-[#0F0E16] dark:text-white"
                        placeholder={t('petOverview.enterYourCity')}
                      />
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSaveLocation}
                      className="flex-1 px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      onClick={handleCancelLocation}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-white/8 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors dark:text-white"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Identification Card */}
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8">
          <h3 className="text-xl mb-4 dark:text-white">
            {t('petOverview.identification')}
          </h3>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.microchipId')}
              </div>
              <div className="font-mono text-sm dark:text-white">
                {pet.microchipId || t('petOverview.na')}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.owner')}
              </div>
              <div className="dark:text-white">{pet.owner.name}</div>
              <div className="text-sm text-gray-500 dark:text-white/60">
                {pet.owner.email}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('petOverview.petDid')}
              </div>
              <div className="font-mono text-xs text-gray-600 dark:text-white/70 break-all">
                {pet.did || t('petOverview.notCreated')}
              </div>
            </div>
          </div>
          <p className="mt-3 pt-3 border-t border-gray-200 dark:border-white/8 text-xs text-gray-500 dark:text-white/50">
            {t(
              'petNameEdit.didMicrochipFixed',
              'DID and Microchip are fixed and cannot be amended. Only the owner can edit the pet name.'
            )}
          </p>
          {effectiveOwnershipOverview && effectiveCurrentUserRole && (
            <OwnershipOverviewCard
              petId={pet.id}
              petName={pet.name}
              ownershipOverview={effectiveOwnershipOverview}
              currentUserRole={effectiveCurrentUserRole}
              onRefresh={refreshOwnership}
            />
          )}
        </div>
      </div>

      {/* Life Events (litters) – for parent pets with offspring */}
      {offspring.length > 0 && (
        <div className="grid md:grid-cols-1 gap-6 mb-8">
          <div
            onClick={() => {
              setSelectedPetId(pet.id);
              navigate({ to: '/dashboard/family' });
            }}
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 border dark:border-white/8 cursor-pointer hover:shadow-lg transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
              <Baby className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold dark:text-white mb-1">
                {t('family.birth.litterBornOn', {
                  date: offspring[0]?.dateOfBirth
                    ? new Date(offspring[0].dateOfBirth).toLocaleDateString()
                    : '—',
                })}
              </h3>
              <p className="text-sm text-gray-600 dark:text-white/70">
                {t('family.birth.puppiesRegistered', {
                  count: offspring.length,
                })}
              </p>
            </div>
            <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">
              {t('family.viewInFamilyTree')}
            </span>
          </div>
        </div>
      )}

      {/* Parents, Ownership & Growth Milestones – for puppy profile */}
      {hasParents && (
        <div className="grid md:grid-cols-1 gap-6 mb-8">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 border dark:border-white/8">
            <h3 className="text-xl mb-4 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('family.birth.parents', 'Parents')} &{' '}
              {t('family.birth.ownership', 'Ownership')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                  {t('family.birth.parents', 'Parents')}
                </div>
                <div className="space-y-2">
                  {motherPet ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPetId(motherPet.id);
                        navigate({ to: '/dashboard' });
                      }}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-900 dark:text-white"
                    >
                      {t('family.birth.mother', 'Mother')}: {motherPet.name}
                    </button>
                  ) : (
                    <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] text-gray-600 dark:text-white/70">
                      {t('family.birth.mother', 'Mother')}:{' '}
                      {pet.parents?.mother
                        ? t('petOverview.linkedNotInYourPets')
                        : '—'}
                    </div>
                  )}
                  {fatherPet ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPetId(fatherPet.id);
                        navigate({ to: '/dashboard' });
                      }}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-900 dark:text-white"
                    >
                      {t('family.birth.father', 'Father')}: {fatherPet.name}
                    </button>
                  ) : (
                    <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] text-gray-600 dark:text-white/70">
                      {t('family.birth.father', 'Father')}:{' '}
                      {pet.parents?.father
                        ? t('petOverview.linkedNotInYourPets')
                        : '—'}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                  {t('family.birth.ownership', 'Ownership')}
                </div>
                <div className="space-y-2">
                  <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] text-gray-900 dark:text-white">
                    {t('family.birth.currentOwner', 'Current owner')}:{' '}
                    {pet.owner?.name ?? '—'}
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#0F0E16] text-gray-900 dark:text-white">
                    {t('family.birth.transferStatus', 'Transfer status')}:{' '}
                    {pet.ownershipStatus === 'pending_transfer'
                      ? t('family.birth.pendingTransfer', 'Pending Transfer')
                      : t('family.birth.active', 'Active')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTransferOwnershipModal(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  {t('family.birth.transferOwnership', 'Transfer Ownership')}
                </button>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/8">
              <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                {t('family.birth.growthMilestones', 'Growth Milestones')}
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm">
                  <Check className="w-4 h-4" />{' '}
                  {t('family.birth.milestoneBorn', 'Born')}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${microchipData ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-white/50'}`}
                >
                  {microchipData ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-current" />
                  )}
                  {t('family.birth.milestoneMicrochipped', 'Microchipped')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-white/50 text-sm">
                  <span className="w-4 h-4 rounded-full border-2 border-current" />
                  {t('family.birth.milestoneVaccinated', 'Vaccinated')}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${pet.ownershipStatus === 'pending_transfer' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-white/50'}`}
                >
                  {pet.ownershipStatus === 'pending_transfer' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-current" />
                  )}
                  {t('family.birth.milestoneTransferred', 'Transferred')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DID Card */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-md p-6 mb-8 text-white relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" />
            <h3 className="text-xl">{t('petOverview.didCardTitle')}</h3>
          </div>

          {/* Status Badge & Menu */}
          <div className="flex items-center gap-3">
            {/* Status Badge */}
            {didStatus === 'active' ? (
              <div className="flex items-center gap-2 bg-green-500/20 border border-green-300/50 px-3 py-1.5 rounded-full">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm font-medium">
                  {t('petOverview.active')}
                </span>
                <span className="text-xs opacity-75">
                  {t('petOverview.verifiedOperational')}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-red-500/20 border border-red-300/50 px-3 py-1.5 rounded-full">
                <div className="w-2 h-2 bg-red-400 rounded-full" />
                <span className="text-sm font-medium">
                  {t('petOverview.didRevoked')}
                </span>
              </div>
            )}

            {/* Dropdown Menu Button */}
            <div className="relative">
              <button
                onClick={() => setShowDIDMenu(!showDIDMenu)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                disabled={didLoading}
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {/* Dropdown Menu */}
              {showDIDMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDIDMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[180px] z-20">
                    {didStatus === 'active' ? (
                      <button
                        onClick={() => {
                          setShowDIDMenu(false);
                          setShowRevokeConfirm(true);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <UserX className="w-5 h-5" />
                        <span className="font-medium">
                          {t('petOverview.revokeDid')}
                        </span>
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          setShowDIDMenu(false);
                          setDidLoading(true);
                          try {
                            await restoreDid(pet.id);
                            setDidStatus('active');
                          } catch (error) {
                            console.error('Failed to restore DID:', error);
                            alert(t('petOverview.failedToRestoreDid'));
                          } finally {
                            setDidLoading(false);
                          }
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-green-600 hover:bg-green-50 transition-colors"
                      >
                        <RefreshCw className="w-5 h-5" />
                        <span className="font-medium">
                          {t('petOverview.restoreDid')}
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDIDMenu(false);
                        alert(t('petOverview.transferOwnershipComingSoon'));
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ArrowRightLeft className="w-5 h-5" />
                      <span className="font-medium">
                        {t('petOverview.transferOwnership')}
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white/10 rounded-lg p-4 mb-4">
          <div className="font-mono text-sm break-all">{pet.did}</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={copyDID}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
          >
            {copied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            <span>
              {copied ? t('common.copied') : t('petOverview.copyDid')}
            </span>
          </button>
          {/* Solana Explorer Link - extract PDA address from DID */}
          {pet.did && pet.did.startsWith('did:petso:') && (
            <a
              href={`https://explorer.solana.com/address/${pet.did.replace('did:petso:', '')}${getSolanaClusterParam()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{t('petOverview.viewOnSolana')}</span>
            </a>
          )}
          <button
            onClick={() => setShowDIDManagement(true)}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
          >
            <Shield className="w-4 h-4" />
            <span>{t('petOverview.manageDid')}</span>
          </button>
        </div>
        <p className="text-sm opacity-90 mt-4">
          {t('petOverview.didDescription')}
        </p>

        {/* Loading Overlay */}
        {didLoading && (
          <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>{t('petOverview.processing')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Revoke DID Confirmation Modal */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRevokeConfirm(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border dark:border-white/10">
            {/* Header with warning icon */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <UserX className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {t('petOverview.revokeDidTitle')}
                  </h3>
                  <p className="text-red-100 text-sm">
                    {t('petOverview.revokeConfirmRequired')}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-white/80 mb-4">
                {t('petOverview.revokeConfirmMessage', { name: pet.name })}
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium mb-1">
                      {t('petOverview.revokeWhatHappens')}
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                      <li>{t('petOverview.revokeBullet1')}</li>
                      <li>{t('petOverview.revokeBullet2')}</li>
                      <li>{t('petOverview.revokeBullet3')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRevokeConfirm(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    setShowRevokeConfirm(false);
                    setDidLoading(true);
                    try {
                      await revokeDid(pet.id);
                      setDidStatus('revoked');
                    } catch (error) {
                      console.error('Failed to revoke DID:', error);
                      alert(t('petOverview.failedToRevokeDid'));
                    } finally {
                      setDidLoading(false);
                    }
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <UserX className="w-4 h-4" />
                  {t('petOverview.confirmRevoke')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pet Profile – same 4 sections as Add Pet */}
      {showEditProfile && (
        <EditPetProfileModal
          pet={pet}
          onClose={() => setShowEditProfile(false)}
        />
      )}

      {/* Report Lost Modal - same as Family Tree */}
      {showReportLostModal && (
        <ReportLostModal
          pet={pet}
          onClose={() => setShowReportLostModal(false)}
          onSuccess={() => {
            setLostStatus('lost');
            setShowReportLostModal(false);
            updatePet(pet.id, { lostStatus: 'lost' });
            refreshPets();
          }}
        />
      )}

      {/* Mark Found Confirmation Modal */}
      {showMarkFoundConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMarkFoundConfirm(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border dark:border-white/10">
            {/* Header with success icon */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <CircleCheck className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {t('petOverview.greatNews')}
                  </h3>
                  <p className="text-green-100 text-sm">
                    {t('petOverview.markFoundConfirmMessage', {
                      name: pet.name,
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-white/80 mb-6">
                {t('petOverview.markFoundConfirmBody', { name: pet.name })}
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMarkFoundConfirm(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    setLostLoading(true);
                    try {
                      await markFound(pet.id);
                      setLostStatus('recovered');
                      setShowMarkFoundConfirm(false);
                      updatePet(pet.id, { lostStatus: 'recovered' });
                      refreshPets();
                    } catch (error) {
                      console.error('Failed to mark pet as found:', error);
                      alert(t('petOverview.failedToMarkFound'));
                    } finally {
                      setLostLoading(false);
                    }
                  }}
                  disabled={lostLoading}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {lostLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CircleCheck className="w-4 h-4" />
                  )}
                  {t('petOverview.confirmFound')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTransferOwnershipModal && (
        <TransferOwnershipModal
          petId={pet.id}
          petName={pet.name}
          onClose={() => setShowTransferOwnershipModal(false)}
          onSuccess={() => {
            refreshPets();
            setShowTransferOwnershipModal(false);
          }}
        />
      )}

      {/* Microchip Status Card - NEW SECTION */}
      <div className="mb-8">
        <MicrochipStatusCard
          petName={pet.name}
          chipData={microchipData}
          onLink={() => setShowMicrochipLinking(true)}
          onManage={() => setShowMicrochipManagement(true)}
        />
      </div>

      {/* Health & Care Records - PROMINENT ACCESS CARD */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-2 border-blue-200 dark:border-blue-700/30 rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl text-blue-900 dark:text-blue-100">
                {t('petOverview.healthCareRecords')}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('petOverview.addAndManageAmendable')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowHealthRecords(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-lg"
          >
            <FileText className="w-5 h-5" />
            {t('petOverview.openRecords')}
          </button>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* DID Status */}
          <div
            onClick={() => !pet.did && setShowDIDCreation(true)}
            className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8 ${!pet.did ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  didStatus === 'revoked'
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-purple-100 dark:bg-purple-900/30'
                }`}
              >
                <Shield
                  className={`w-5 h-5 ${
                    didStatus === 'revoked'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-purple-600 dark:text-purple-400'
                  }`}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('petOverview.digitalId')}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-white/8 pt-4 mt-auto">
              <div
                className={`text-2xl mb-1 ${
                  didStatus === 'revoked'
                    ? 'text-red-600 dark:text-red-400'
                    : 'dark:text-white'
                }`}
              >
                {!pet.did
                  ? t('petOverview.createDid', 'Create')
                  : didStatus === 'active'
                    ? t('petOverview.active')
                    : t('petOverview.didRevoked')}
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {!pet.did
                  ? t('petOverview.setUpDid')
                  : didStatus === 'active'
                    ? t('petOverview.verifiedIdentity')
                    : t('petOverview.identityRevoked')}
              </p>
            </div>
          </div>

          {/* NFT Status */}
          <div
            onClick={() =>
              (pet.nfts?.length || 0) > 0
                ? setShowNFTGallery(true)
                : setShowMintNFT(true)
            }
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-pink-600 dark:text-pink-400" />
              </div>
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('petOverview.nfts')}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-white/8 pt-4 mt-auto">
              <div className="text-2xl mb-1 dark:text-white">
                {(pet.nfts?.length || 0) > 0
                  ? t('petOverview.active')
                  : t('petOverview.createDid', 'Create')}
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {(pet.nfts?.length || 0) > 0
                  ? t('petOverview.nftsMinted')
                  : t('petOverview.mintNfts')}
              </p>
            </div>
          </div>

          {/* Microchip Status */}
          <div
            onClick={() =>
              microchipData
                ? setShowMicrochipManagement(true)
                : setShowMicrochipLinking(true)
            }
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Cpu className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('petOverview.microchip')}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-white/8 pt-4 mt-auto">
              <div className="text-2xl mb-1 dark:text-white">
                {microchipData
                  ? t('petOverview.active')
                  : t('petOverview.link')}
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {microchipData
                  ? t('petOverview.microchipLinked')
                  : t('petOverview.linkMicrochip')}
              </p>
            </div>
          </div>

          {/* Health Records Status */}
          <div
            onClick={() => setShowHealthRecords(true)}
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 flex flex-col h-full border dark:border-white/8 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('petOverview.healthRecords')}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-white/8 pt-4 mt-auto">
              <div className="text-2xl mb-1 dark:text-white">
                {(pet.healthRecords?.length || 0) > 0
                  ? t('petOverview.active')
                  : t('petOverview.add')}
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {(pet.healthRecords?.length || 0) > 0
                  ? t('petOverview.recordsAvailable')
                  : t('petOverview.addRecords')}
              </p>
            </div>
          </div>
        </div>

        {/* Blockchain Notice */}
        <div className="mt-4 flex items-start gap-3 bg-white/60 dark:bg-white/5 rounded-lg p-3 border border-blue-200 dark:border-blue-700/30">
          <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-700 dark:text-white/70">
            <span className="text-purple-700 dark:text-purple-400">
              {t('petOverview.blockchainSecured')}
            </span>{' '}
            {t('petOverview.blockchainNotice', { name: pet.name })}
          </div>
        </div>
      </div>

      {/* Health Summary */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Vaccination Status */}
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 border dark:border-white/8">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xl dark:text-white">
              {t('petOverview.vaccinationStatus')}
            </h3>
          </div>
          <div className="space-y-3">
            {vaccinations.map((vac) => {
              const nextDue = new Date(vac.nextDue);
              const today = new Date();
              const daysUntilDue = Math.floor(
                (nextDue.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
              );
              const isOverdue = daysUntilDue < 0;
              const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 30;

              return (
                <div
                  key={vac.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg"
                >
                  <div>
                    <div className="dark:text-white">{vac.vaccine}</div>
                    <div className="text-sm text-gray-500 dark:text-white/50">
                      {t('petOverview.nextDue', {
                        date: new Date(vac.nextDue).toLocaleDateString(),
                      })}
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm ${
                      isOverdue
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        : isDueSoon
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    }`}
                  >
                    {isOverdue
                      ? t('petOverview.overdue')
                      : isDueSoon
                        ? t('petOverview.dueSoon')
                        : t('petOverview.current')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 border dark:border-white/8">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xl dark:text-white">
              {t('petOverview.recentActivity')}
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex gap-3 p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
              <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full mt-2" />
              <div className="flex-1">
                <div className="dark:text-white">
                  {t('petOverview.healthCertificateAdded')}
                </div>
                <div className="text-sm text-gray-500 dark:text-white/50">
                  {t('petOverview.daysAgo', { count: 3 })}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
              <div className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full mt-2" />
              <div className="flex-1">
                <div className="dark:text-white">
                  {t('petOverview.vaccinationRecordUpdated')}
                </div>
                <div className="text-sm text-gray-500 dark:text-white/50">
                  {t('petOverview.weekAgo')}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
              <div className="w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full mt-2" />
              <div className="flex-1">
                <div className="dark:text-white">
                  {t('petOverview.appointmentCompleted')}
                </div>
                <div className="text-sm text-gray-500 dark:text-white/50">
                  {t('petOverview.weeksAgo', { count: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Approvals Banner */}
      {pendingProposals.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <h3 className="text-xl mb-1">
                  {t('petOverview.pendingApprovalCount', {
                    count: pendingProposals.length,
                  })}
                </h3>
                <p className="text-sm text-orange-100">
                  {t('petOverview.professionalProposedRecords', {
                    count: pendingProposals.length,
                    name: pet.name,
                  })}
                </p>
                <div className="flex gap-2 mt-2">
                  {pendingProposals.slice(0, 3).map((record) => (
                    <span
                      key={record.id}
                      className="text-xs bg-white/20 px-2 py-1 rounded"
                    >
                      {record.recordType === 'vaccine' && '💉'}
                      {record.recordType === 'grooming' && '✂️'}
                      {record.recordType === 'treatment' && '🏥'}{' '}
                      {record.recordType}
                    </span>
                  ))}
                  {pendingProposals.length > 3 && (
                    <span className="text-xs bg-white/20 px-2 py-1 rounded">
                      {t('petOverview.moreCount', {
                        count: pendingProposals.length - 3,
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowPendingApprovals(true)}
              className="flex items-center gap-2 bg-white dark:bg-white/10 text-orange-600 dark:text-orange-300 px-6 py-3 rounded-lg hover:bg-orange-50 dark:hover:bg-white/20 transition-colors whitespace-nowrap"
            >
              <Bell className="w-5 h-5" />
              {t('petOverview.reviewNow')}
            </button>
          </div>
        </div>
      )}

      {/* Link Microchip modal overlay */}
      {showMicrochipLinking && (
        <MicrochipLinking
          petId={pet.id}
          petName={pet.name}
          hasDID={!!pet.did}
          onComplete={async (data) => {
            setMicrochipLoading(true);
            try {
              await linkMicrochip(pet.id, {
                chipNumber: data.chipNumber,
                standard: data.standard,
                implantDate: data.implantDate,
                implantLocation: data.implantLocation,
                linkToDID: data.linkToDID,
                visibility: data.visibility,
              });
              setMicrochipData({
                ...data,
                linkedDate: new Date().toISOString(),
              });
              setShowMicrochipLinking(false);
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
            } catch (error) {
              console.error('Failed to link microchip:', error);
              setMicrochipData({
                ...data,
                linkedDate: new Date().toISOString(),
              });
              setShowMicrochipLinking(false);
            } finally {
              setMicrochipLoading(false);
            }
          }}
          onCancel={() => setShowMicrochipLinking(false)}
        />
      )}

      {/* Manage Microchip modal overlay */}
      {showMicrochipManagement && microchipData && (
        <MicrochipManagement
          petName={pet.name}
          chipData={microchipData}
          onUpdate={async (updates) => {
            setMicrochipLoading(true);
            try {
              await updateMicrochipSettings(pet.id, {
                chipNumber: microchipData.chipNumber,
                standard: updates.standard || microchipData.standard,
                implantDate: updates.implantDate || microchipData.implantDate,
                implantLocation:
                  updates.implantLocation || microchipData.implantLocation,
                linkToDID:
                  updates.linkToDID !== undefined
                    ? updates.linkToDID
                    : microchipData.linkToDID,
                visibility: updates.visibility || microchipData.visibility,
              });
              setMicrochipData({ ...microchipData, ...updates });
              setShowMicrochipManagement(false);
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
            } catch (error) {
              console.error('Failed to update microchip:', error);
              setMicrochipData({ ...microchipData, ...updates });
              setShowMicrochipManagement(false);
            } finally {
              setMicrochipLoading(false);
            }
          }}
          onUnlink={async () => {
            setMicrochipLoading(true);
            try {
              await unlinkMicrochipApi(pet.id);
              setMicrochipData(null);
              setShowMicrochipManagement(false);
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
            } catch (error) {
              console.error('Failed to unlink microchip:', error);
              setMicrochipData(null);
              setShowMicrochipManagement(false);
            } finally {
              setMicrochipLoading(false);
            }
          }}
          onClose={() => setShowMicrochipManagement(false)}
        />
      )}

      {/* Profile photo crop & edit */}
      {photoToEdit && (
        <MediaEditorModal
          imageUrl={photoToEdit.previewUrl}
          isVideo={false}
          onClose={() => {
            URL.revokeObjectURL(photoToEdit.previewUrl);
            setPhotoToEdit(null);
          }}
          onDone={(editedFile) => {
            URL.revokeObjectURL(photoToEdit.previewUrl);
            setPhotoToEdit(null);
            uploadPetPhotoFile(editedFile);
          }}
        />
      )}
    </div>
  );
}
